<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <v-container v-show="!locationsLoading" fluid class="fill-height pt-0">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-if="consumptionMode === ticketBased"
              v-model="search"
              data-testid="consume-ticket-search"
              :label="$t('search')"
              color="primary"
              clearable
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="auto">
            <span class="font-italic">
              {{consumptionModeText}}
            </span>
          </v-col>
          <v-spacer></v-spacer>
          <v-col v-if="consumptionMode === ticketBased" cols="auto" class="mt-4">
            <ColumnAdjuster
              :propColumnSet.sync="columnSet"
              @new-columns-selected="newColumnsSelected"
            />
            <v-menu
            bottom
            left
            v-model="menuOpen">
              <template #activator="{ on, attrs}">
                <v-icon
                color="primary"
                v-bind="attrs"
                v-on="on">mdi-download</v-icon>
              </template>
              <v-list>
                <v-list-item
                v-for="(action, index) in actions"
                :key="`download-action-${index}`"
                :disabled="action.disabled"
                @click="action.onClick(action.clickArgument)">
                  <v-avatar left >
                    <v-icon
                    :color="action.isEnabled ? 'grey' : 'black'">
                      {{action.icon}}
                    </v-icon>
                  </v-avatar>
                  <v-list-item-title class="subtitle-1">
                    {{action.text}}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
        <v-row v-if="consumptionMode === ticketBased">
          <v-col cols="12">
            <v-data-table
              v-model="selected"
              :items="formattedTicketsForConsumption"
              :search="search"
              show-select
              dense
              :footer-props="{
                'items-per-page-options': dataTableItemsPerPage
              }"
              :items-per-page="defaultItemsPerPage"
              @update:items-per-page="updateCurrentItemsPerPage"
              item-key="ticketId"
              :headers="gridHeaders"
              @update:sort-by="updateSort('by', $event)"
              @update:sort-desc="updateSort('desc', $event)"
              fixed-header
              :height="tableHeight"
              checkbox-color="success"
            >
              <template #item.extTicketNumber1="{item}">
                <span>
                  {{ item.extTicketNumber1 !== '' ? item.extTicketNumber1 : '-' }}
                </span>
              </template>
              <template #item.extTicketNumber2="{item}">
                <span>
                  {{ item.extTicketNumber2 !== '' ? item.extTicketNumber2 : '-' }}
                </span>
              </template>
              <template #item.source="{ item }">
                <span
                  class="flex-nowrap d-flex"
                  style="text-wrap-mode: nowrap;"
                >
                  <v-tooltip bottom color="secondary">
                    <template #activator="{ on }">
                      <v-icon
                      v-on="on"
                      color="grey darken-3"
                      small
                      class="mr-1"
                      >
                      {{ !!item.tract ? 'mdi-crosshairs-gps' : 'mdi-account-arrow-right' }}
                      </v-icon>
                    </template>

                    <span class="subtitle-1 white--text">
                      {{ !!item.tract ? $t('tract') : $t('fromAccount') }}
                    </span>
                  </v-tooltip>
                  {{ item.tract ?? item.fromAccount }}
                </span>
              </template>
              <template #item.mbf="{item}">
                <span>
                  {{ formatMbf(item.mbf) }}
                </span>
              </template>
              <template #item.weighedOutAt="{ item }">{{
                utcToLocalDate(item.weighedOutAt, 'L - LT')
              }}</template>
              <template #item.deckAssignedAt="{ item }">
                <span>{{ utcToLocalDate(item.deckAssignedAt, 'L - LT') }}</span>
              </template>
              <template #header.data-table-select>
                <Icon
                top
                :icon="iconForSelectAllBox"
                :iconColor="allSelected ? 'success' : 'rgba(0,0,0,0.3)'"
                :tooltipText="allSelected ? $t('deselectAll') : $t('selectAll')"
                tooltipColor="secondary"
                :small="false"
                @icon-clicked="selectAllChanged(!allSelected)"
                :disabled="selectAllDisabled"
                :style="selectAllStyle"
                />
              </template>
              <template #footer.prepend>
                <v-col cols="auto">
                  <v-row align="center" dense>
                    <v-col cols="12" sm="auto" v-if="consumptionMode === ticketBased">
                      <v-chip :color="allSelected ? 'success' : ''">
                        <span>
                          {{ `${$t('selected')}: ${selected.length} / ${ticketsForConsumption.length}` }}
                        </span>
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-col>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" md="6">
            <v-text-field
              v-if="consumptionMode === weightBased"
              :label="$t('onDeckLbs')"
              outlined
              :value="onDeck"
              color="primary"
              readonly
            />
            <v-text-field
              v-else
              :label="$t('onDeckMbf')"
              outlined
              :value="onDeckMbf"
              color="primary"
              readonly
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-if="consumptionMode === weightBased"
              v-model="weightToConsume"
              data-testid="consume-weight"
              :label="$t('weight')"
              color="primary"
              type="number"
              hide-spin-buttons
            />
          </v-col>
        </v-row>
        <v-row>
          <Dialog :stateId="dialogId" @close="closeDialog">
            <ConfirmConsumption
              v-if="confirmConsumptionDialog"
              :deck="deck"
              :weightToConsume="parsedWeightToConsume"
              :ticketsForConsumption="selected"
              :productMap="productIdDictionary"
              @close="closeDialog"
              @tickets-consumed="ticketsConsumed"
            />
            <ConfirmDialog
              v-if="confirmTransferDialog"
              :title="consumptionMode === ticketBased ? $t('transferTickets') : $t('transferWeight')"
              :confirmText="consumptionMode === ticketBased ? $t('transferTickets') : $t('transferWeight')"
              body=""
              width="700px"
              color="primary"
              @confirm="transferTickets"
              @cancel="closeDialog"
            >
              <template #custom-body>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <span class="font-weight-bold">{{ $t('selectDeck') }}:</span>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12">
                    <v-card elevation="0">
                      <FilteredDeckSelector
                        limitToOpen
                        :showAll="false"
                        :propInitialLocationId="deck?.locationId"
                        :currentDeckId="deck.deckId"
                        :consumptionMode="consumptionMode"
                        @deck-selected="destinationDeckSelected"
                        :contentType="deck.contentType"
                        :productIds="selectedProductIds()"
                      />
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </ConfirmDialog>
          </Dialog>
        </v-row>
      </v-container>
      <v-container fluid v-if="locationsLoading">
        <v-row align="center" justify="center" class="pa-12 ma-12">
          <v-progress-circular indeterminate color="primary"/>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions class="mt-n2 pb-4">
      <v-row align="center">
        <v-col>
          <AggregateTotalTable
            v-if="consumptionMode === ticketBased"
            :aggregateTotal="aggregateTotal"
          />
        </v-col>

        <v-col cols="auto">
          <v-row align="center" dense>
            <v-col cols="auto">
              <v-row align="center" dense>
                <v-col>
                  <v-btn
                  v-if="consumptionMode !== boardFoot"
                  block
                  @click="openConfirmTransferDialog"
                  :disabled="submitButtonsDisabled"
                  class="primary">
                    <v-icon class="mr-1">mdi-transfer</v-icon>
                    {{transferButtonText}}
                  </v-btn>
                </v-col>

                <v-col>
                  <v-btn
                  v-if="consumptionMode !== boardFoot"
                  block
                  @click="openConfirmConsumptionDialog"
                  :disabled="submitButtonsDisabled"
                  class="primary">
                    <v-icon class="mr-1">mdi-saw-blade</v-icon>
                    {{submitButtonText}}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>

      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { tonStringFromPounds, numberWithCommas } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import TicketHeaders from '@/headers/Ticket'
import { ConsumptionMode, ContractMode, DeckContentType } from '@/utils/Enumerations'
import { generatePdf } from '@/utils/pdf-formatters/TicketsOnDeck.js'
import { generateCsvString } from '@/utils/CSVUtility'
import { saveAs } from 'file-saver'
import { localeCompareAlphanumeric } from '@/utils/base'
import { dataTableItemsPerPage, dataTableDefaultItemsPerPage, RowHeights } from '@/utils/componentHelpers'
import { cloneDeep } from 'lodash'

export default {
  name: 'ConsumeTickets',

  async created () {
    this.initializeHeaders()
    await this.fetchLocations({ includeByproductDecks: true, includeDeckLoadCounts: true })
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FilteredDeckSelector: () => import('@/components/dashboard/FilteredDeckSelector.vue'),
    ConfirmConsumption: () => import('@/components/ticket/ticket-consumption/ConfirmConsumption.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    AggregateTotalTable: () => import('@/components/ticket/ticket-consumption/AggregateTotalTable.vue')
  },

  props: {
    refreshFlag: {
      type: Boolean,
      default: false
    },
    productIdDictionary: {
      type: Object,
      default: () => ({})
    },
    contractMode: {
      type: Object,
      default: undefined
    },
    deck: {
      type: Object,
      default: undefined
    },
    entity: {
      type: Object,
      default: undefined
    }
  },

  data: () => ({
    dataTableItemsPerPage,
    defaultItemsPerPage: dataTableDefaultItemsPerPage,
    dialogId: 'consumption',
    confirmConsumptionDialog: false,
    confirmTransferDialog: false,
    weightToConsume: 0,
    deckId: null,
    destinationDeckId: null,
    search: '',
    columnSet: null,
    pdfColumnSort: undefined,
    sortDirection: undefined,
    selected: [],
    menuOpen: false,
    DeckContentType,
    allSelected: false,
    currentItemsPerPage: dataTableDefaultItemsPerPage
  }),

  computed: {
    ...mapGetters('consumption', ['ticketsForConsumption']),
    ...mapGetters('locations', ['allLocations', 'locationsLoading']),
    ...mapGetters('dialog', ['topDialogState']),

    headers () {
      return TicketHeaders.ticketConsumptionHeaders()
    },

    consumptionMode () {
      return this.deck?.consumptionMode ?? 0
    },

    ticketBased () {
      return ConsumptionMode.TicketBased.value
    },

    weightBased () {
      return ConsumptionMode.WeightBased.value
    },

    boardFoot () {
      return ConsumptionMode.BoardFoot.value
    },

    parsedWeightToConsume () {
      return Number(this.weightToConsume)
    },

    contentType () {
      return this.contractMode === undefined || this.contractMode.value !== ContractMode.Byproducts.value
        ? DeckContentType.Logs
        : DeckContentType.Byproducts
    },

    aggregateTotal () {
      let averageRpiFlag = true
      const totals = this.selected.reduce((t, ticket) => {
        t.weightTotal += ticket.inWeight - ticket.outWeight - ticket.defectWeight
        t.mbfTotal += (ticket.inWeight - ticket.outWeight - ticket.defectWeight) * this.productIdDictionary[ticket.product].conversionRateW2V
        if (ticket.rpi === 0) {
          averageRpiFlag = false
        } else {
          t.rpiTotal += (ticket.rpi * (ticket.inWeight - ticket.outWeight - ticket.defectWeight))
        }
        return t
      }, { weightTotal: 0, mbfTotal: 0, rpiTotal: 0 })

      return {
        weightTons: tonStringFromPounds(totals.weightTotal),
        mbf: numberWithCommas(Math.ceil(totals.mbfTotal)),
        rpi: averageRpiFlag ? numberWithCommas((totals.rpiTotal / totals.weightTotal), 2) : 0
      }
    },

    submitButtonsDisabled () {
      return this.consumptionMode === ConsumptionMode.TicketBased.value
        ? this.selected.length === 0
        : this.weightToConsume <= 0 || (this.deck?.onDeck ?? 0) < this.weightToConsume * 2000
    },

    transferButtonText () {
      return this.consumptionMode === ConsumptionMode.TicketBased.value
        ? this.$t('transferSelected')
        : this.$t('transferWeight')
    },

    submitButtonText () {
      return this.consumptionMode === ConsumptionMode.TicketBased.value
        ? this.$t('consumeSelected')
        : this.$t('consumeWeight')
    },

    consumptionModeText () {
      let consumptionModeText
      switch (this.consumptionMode) {
        case ConsumptionMode.TicketBased.value:
          consumptionModeText = this.$t('ticketBased')
          break
        case ConsumptionMode.WeightBased.value:
          consumptionModeText = this.$t('weightBased')
          break
        case ConsumptionMode.BoardFoot.value:
          consumptionModeText = this.$t('boardFootBased')
          break
        default:
          consumptionModeText = this.$t('weightBased')
      }
      return this.$t('consumptionModeType', { type: consumptionModeText })
    },

    onDeck () {
      return this.deck !== undefined ? tonStringFromPounds(this.deck.onDeck) : 0
    },

    onDeckMbf () {
      return this.deck !== undefined ? numberWithCommas(this.deck.onDeck / 1000, 3) : 0
    },

    gridHeaders () {
      return this.headers.getColumns() ?? []
    },

    actions () {
      return [
        {
          text: this.$t('downloadCSV'),
          icon: 'mdi-file-delimited-outline',
          onClick: this.downloadCsv,
          disabled: false
        },
        {
          text: this.$t('downloadPDF'),
          icon: 'mdi-file-pdf-box',
          onClick: this.downloadPDF,
          disabled: false
        }
      ]
    },

    formattedTicketsForConsumption () {
      const tickets = this.ticketsForConsumption
      tickets.forEach(ticket => {
        ticket.netWeight = this.getNetWeight(ticket)
        ticket.mbf = this.calculateTicketMbf(ticket)
      })

      return tickets
    },

    tableHeight () {
      const items = this.formattedTicketsForConsumption
      const itemCount = items.length
      const tableHeightEstimate = Math.min(this.currentItemsPerPage, itemCount) * (RowHeights.Dense.timesWrapped(1))
      return `max(256px, min(${tableHeightEstimate}px, calc(${this.topDialogState?.fullscreen ? 100 : 90}vh - 440px)))`
    },

    iconForSelectAllBox () {
      return this.allSelected ? 'mdi-checkbox-multiple-marked' : this.selected.length > 0 ? 'mdi-minus-box-multiple' : 'mdi-checkbox-multiple-blank-outline'
    },

    selectAllDisabled () {
      return !!this.search
    },

    selectAllStyle () {
      return this.selectAllDisabled ? 'pointer-events: none; opacity: 0.25;' : ''
    }
  },

  watch: {
    refreshFlag (val) {
      if (val === true) {
        this.fetchLocations({ includeByproductDecks: true, includeDeckLoadCounts: true })
      }
    },
    deck: {
      handler (val, oldVal) {
        if (val?.deckId === oldVal?.deckId) return
        this.refresh()
      },
      deep: true,
      immediate: true
    },
    selected (val) {
      this.allSelected = this.ticketsForConsumption?.length > 0 && val.length === this.ticketsForConsumption.length
    }
  },
  methods: {
    ...mapActions('locations', ['fetchLocations']),
    ...mapActions('consumption', ['fetchTicketsForConsumption', 'fetchConsumptionBatches', 'postConsumptionBatch', 'revertTickets', 'postTransferBatches']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    utcToLocalDate,

    destinationDeckSelected (deck) {
      this.destinationDeckId = deck.deckId
    },

    async transferTickets () {
      if (this.destinationDeckId === null || this.destinationDeckId === undefined) {
        this.setSnackError(this.$t('noDeckSelected'))
        return
      }
      const ticketIds = this.selected.map(t => t.ticketId)
      const requestObj = {
        ticketIds: this.consumptionMode === ConsumptionMode.TicketBased.value ? ticketIds : [],
        destinationDeckId: this.destinationDeckId,
        fromDeckId: this.deck.deckId,
        weight: this.consumptionMode === ConsumptionMode.WeightBased.value ? Math.round(this.weightToConsume * 2000) : null
      }
      await this.postTransferBatches(requestObj)
      this.refresh()
      this.closeDialog()
      this.$emit('trigger-refresh')
    },

    deckSelected (deck) {
      this.deck = deck
      this.refresh()
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    initializeHeaders () {
      this.columnSet = TicketHeaders.ticketConsumptionHeaders()
    },

    refresh () {
      this.selected = []
      this.weightToConsume = 0
      this.fetchTicketsForConsumption(this.deck.deckId)
    },

    ticketsConsumed () {
      this.closeDialog()
      this.$emit('trigger-refresh')
      this.refresh()
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.destinationDeckId = null
      this.confirmTransferDialog = false
      this.confirmConsumptionDialog = false
    },

    openConfirmTransferDialog () {
      if (this.allLocations.length === 1 && this.allLocations[0].decks?.filter(deck => deck.status === 0).length === 1) {
        this.setSnackError(this.$t('oneDeckTransfer'))
        return
      }
      this.confirmTransferDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '700px', allowFullscreen: false })
    },

    openConfirmConsumptionDialog () {
      this.confirmConsumptionDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '500px', allowFullscreen: false })
    },

    getNetWeight (item) {
      const netWeight = item.inWeight - item.outWeight - item.defectWeight
      if (item.inWeight <= 0) {
        return this.$t('notAvailible')
      } else {
        return tonStringFromPounds(netWeight)
      }
    },

    calculateTicketMbf (ticket) {
      return Math.ceil((ticket.inWeight - ticket.outWeight - ticket.defectWeight) * this.productIdDictionary[ticket.product].conversionRateW2V)
    },

    formatMbf (mbf) {
      return numberWithCommas(mbf)
    },

    updateSort (byDesc, event) {
      if (byDesc === 'by') {
        this.pdfColumnSort = event
      } else if (byDesc === 'desc') {
        this.sortDirection = event
      }
    },

    async downloadPDF () {
      const tickets = this.getSortedTickets()

      await generatePdf({
        tickets: tickets,
        deck: this.deck.name,
        location: this.deck.locationName,
        entity: this.entity.name,
        headers: this.columnSet.getColumns(),
        products: this.productIdDictionary
      })
    },

    getSortedTickets () {
      const tickets = this.ticketsForConsumption
      if (this.sortDirection || this.pdfColumnSort) {
        tickets.sort((a, b) => {
          const aValue = a[this.pdfColumnSort].toString()
          const bValue = b[this.pdfColumnSort].toString()
          return localeCompareAlphanumeric(aValue, bValue) * (this.sortDirection ? -1 : 1)
        })
      }
      return tickets
    },

    downloadCsv () {
      const tickets = this.getSortedTickets()
      const csvString = generateCsvString(
        tickets,
        this.columnsForCsv().map(h => ({
          text: h.text,
          value: t => this.csvValueOf(t, h)
        })),
        { emptyValue: this.$t('notAvailable') }
      )

      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, `${this.entity.name}_${this.deck.locationName}_${this.deck.name}.csv`)
    },

    columnsForCsv () {
      const cols = cloneDeep(this.columnSet.getColumns())
      cols.splice(
        cols.findIndex(h => h.value === 'source'),
        1,
        {
          text: this.$t('tract'),
          value: 'tract'
        }, {
          text: this.$t('fromAccount'),
          value: 'fromAccount'
        })
      return cols
    },

    csvValueOf (ticket, header) {
      switch (header.value) {
        case 'netWeight':
          const netWeight = ticket.inWeight - ticket.outWeight - ticket.defectWeight
          if (ticket.inWeight <= 0) {
            return 'N/A'
          } else {
            return tonStringFromPounds(netWeight)
          }
        case 'mbf':
          return this.calculateTicketMbf(ticket)
        case 'deckAssignedAt' || 'weighedOutAt':
          return utcToLocalDate(ticket[header.value], 'YYYY-MM-DDTHH:mm:SS')
        default:
          return ticket[header.value]
      }
    },

    selectedProductIds () {
      if (this.confirmTransferDialog && this.consumptionMode === ConsumptionMode.WeightBased.value) {
        return this.deck.deckContents.map(dc => dc.productId)
      } else if (this.consumptionMode === ConsumptionMode.TicketBased.value) {
        const selectedTicketsProductIds = this.selected.map(t => t.productId)
        return selectedTicketsProductIds
      }

      return []
    },

    selectAllChanged (v) {
      this.selected = v ? this.ticketsForConsumption : []
    },

    updateCurrentItemsPerPage (itemsPerPage) {
      this.currentItemsPerPage = itemsPerPage
    }
  }
}
</script>
